import { slugify } from "@/utils/projectRoutes";

export const CONTENT_FORMAT_IMAGE_PAGES = "image_pages";
export const CONTENT_FORMAT_RICH_TEXT = "rich_text";

export const READING_MODE_VERTICAL = "Vertical";
export const READING_MODE_HORIZONTAL = "Horizontal";

export const PROFILE_CATEGORY_ORDER = ["quadrinhos", "webtoon", "livros"];

const VALID_CONTENT_FORMATS = [CONTENT_FORMAT_IMAGE_PAGES, CONTENT_FORMAT_RICH_TEXT];
const IMAGE_PAGES_SLUGS = ["quadrinhos", "quadrinho", "webtoon"];
const RICH_TEXT_SLUGS = ["livros", "livro"];

/**
 * Compatibilidade transitória com payload legado (sem slug/content_format).
 * Isolada aqui: nenhum componente deve usar ID ou nome para decidir jornada.
 */
const LEGACY_FORMAT_BY_CATEGORY_ID = {
  1: CONTENT_FORMAT_IMAGE_PAGES,
  3: CONTENT_FORMAT_RICH_TEXT,
};

export function normalizeContentFormat(value) {
  const format = typeof value === "string" ? value.trim().toLowerCase() : "";
  return VALID_CONTENT_FORMATS.includes(format) ? format : null;
}

function resolveRawCategory(source) {
  if (!source) return null;
  if (source.project_category && typeof source.project_category === "object") return source.project_category;
  if (source.category && typeof source.category === "object") return source.category;
  if (source.content_format !== undefined || source.project_types_count !== undefined) return source;
  return null;
}

function inferLegacyContentFormat({ id, slug, name }) {
  const normalizedSlug = slugify(slug || name || "");
  if (RICH_TEXT_SLUGS.includes(normalizedSlug)) return CONTENT_FORMAT_RICH_TEXT;
  if (IMAGE_PAGES_SLUGS.includes(normalizedSlug)) return CONTENT_FORMAT_IMAGE_PAGES;

  const numericId = Number(id);
  if (Number.isFinite(numericId) && LEGACY_FORMAT_BY_CATEGORY_ID[numericId]) {
    return LEGACY_FORMAT_BY_CATEGORY_ID[numericId];
  }

  return null;
}

export function getProjectCategoryDescriptor(source) {
  if (!source) return null;

  const raw = resolveRawCategory(source);
  const rawId = raw ? raw.id : source.project_category_id ?? source.category_id;
  const id = Number.isFinite(Number(rawId)) ? Number(rawId) : null;
  const rawName = raw ? raw.name : typeof source.category === "string" ? source.category : "";
  const name = rawName || source.category_name || "";
  const slug = (raw && raw.slug) || source.category_slug || (name ? slugify(name) : "");
  const contentFormat =
    normalizeContentFormat(raw && raw.content_format) || inferLegacyContentFormat({ id, slug, name });

  if (id === null && !name && !contentFormat) return null;

  return { id, name, slug, contentFormat };
}

export function getCategoryContentFormat(source) {
  const descriptor = getProjectCategoryDescriptor(source);
  return descriptor ? descriptor.contentFormat : null;
}

export function getCategoryLabel(source, fallback = "Obra") {
  const descriptor = getProjectCategoryDescriptor(source);
  return (descriptor && descriptor.name) || fallback;
}

export function isImagePages(source) {
  return getCategoryContentFormat(source) === CONTENT_FORMAT_IMAGE_PAGES;
}

export function isRichText(source) {
  return getCategoryContentFormat(source) === CONTENT_FORMAT_RICH_TEXT;
}

export function getReadingPath(source, chapterId) {
  if (!chapterId) return null;
  if (isRichText(source)) return `/obra/book/reading/${chapterId}`;
  return `/${chapterId}/page`;
}

export function getChapterManagerPath(project) {
  if (!project || !project.id) return null;
  if (isRichText(project)) return `/dashboard/criar-capitulo-livro/${project.id}`;
  return `/dashboard/obrar/${project.id}`;
}

export function getDefaultReadingMode(source) {
  return isImagePages(source) ? READING_MODE_VERTICAL : null;
}

export function pickAvailableCategories(categories = []) {
  return (Array.isArray(categories) ? categories : []).filter((category) => {
    if (!normalizeContentFormat(category && category.content_format)) return false;
    if (category.available === false) return false;

    const typesCount = category.project_types_count;
    if (typesCount !== undefined && typesCount !== null && Number(typesCount) <= 0) return false;

    return true;
  });
}

export function pickFilterCategories(categories = []) {
  return (Array.isArray(categories) ? categories : []).filter((category) =>
    Boolean(normalizeContentFormat(category && category.content_format))
  );
}

export function findCategoryBySlug(categories = [], slug) {
  if (!slug) return null;
  return (Array.isArray(categories) ? categories : []).find((category) => category.slug === slug) || null;
}

function sectionOrder(section, catalog) {
  const knownIndex = PROFILE_CATEGORY_ORDER.indexOf(section.slug);
  if (knownIndex !== -1) return knownIndex;

  const catalogIndex = catalog.findIndex((category) => category.slug === section.slug);
  if (catalogIndex !== -1) return PROFILE_CATEGORY_ORDER.length + catalogIndex;

  return Number.MAX_SAFE_INTEGER;
}

export function buildProfileCategorySections(projects = [], categories = []) {
  const catalog = Array.isArray(categories) ? categories : [];
  const buckets = new Map();

  for (const project of Array.isArray(projects) ? projects : []) {
    const descriptor = getProjectCategoryDescriptor(project);
    const slug = (descriptor && descriptor.slug) || (descriptor && descriptor.id !== null ? `categoria-${descriptor.id}` : "outras");
    const key = slug || "outras";

    if (!buckets.has(key)) {
      const catalogMatch = catalog.find(
        (category) => category.slug === key || `categoria-${category.id}` === key
      );

      buckets.set(key, {
        key,
        slug,
        name: (catalogMatch && catalogMatch.name) || (descriptor && descriptor.name) || "Obras",
        contentFormat:
          (descriptor && descriptor.contentFormat) ||
          normalizeContentFormat(catalogMatch && catalogMatch.content_format),
        projects: [],
      });
    }

    const bucket = buckets.get(key);
    if (!bucket.projects.some((item) => item.id === project.id)) {
      bucket.projects.push(project);
    }
  }

  return Array.from(buckets.values())
    .filter((section) => section.projects.length > 0)
    .sort((left, right) => {
      const orderDiff = sectionOrder(left, catalog) - sectionOrder(right, catalog);
      if (orderDiff !== 0) return orderDiff;
      return left.name.localeCompare(right.name);
    });
}
